import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const REELS = [
  {
    id: 1,
    title: "Ubikalo - Historias de Xela",
    subtitle: "Ubikalo - Historias de Xela",
    sources: [
      { src: "https://ubikalo.app/videos/edison.mp4", type: "video/mp4" },
    ],
    poster: "/assets/images/portada.jpg",
  },
  {
    id: 2,
    title: "Ubikalo - Soy Sara",
    subtitle: "Ubikalo - Soy Sara",
    sources: [
      { src: "https://ubikalo.app/videos/sara.mp4", type: "video/mp4" },
    ],
    poster: "/assets/images/1.png",
  },
  {
    id: 3,
    title: "Ubikalo - Kimichi",
    subtitle: "Ubikalo - Kimichi",
    sources: [
      { src: "https://ubikalo.app/videos/kim.mp4", type: "video/mp4" },
    ],
    poster: "/assets/images/2.png",
  },
  {
    id: 4,
    title: "Conoce Ubikalo",
    subtitle: "Conoce Ubikalo",
    sources: [
      { src: "https://ubikalo.app/videos/dayana.mp4", type: "video/mp4" },
    ],
    poster: "/assets/images/3.png",
  },
];

const ReelCard = ({ reel, onOpen }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleTimeUpdate = () => {
      if (video.currentTime >= 2) {
        video.currentTime = 0.05;
      }
    };

    const handleLoaded = () => {
      try {
        video.currentTime = 0.05;
      } catch (e) {}
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoaded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoaded);
    };
  }, []);

  return (
    <button type="button" className="home-reels__card" onClick={() => onOpen(reel)}>
      <video
        ref={videoRef}
        className="home-reels__video"
        poster={reel.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      >
        {reel.sources.map((source, index) => (
          <source key={`${source.src}-${index}`} src={source.src} type={source.type} />
        ))}
      </video>
      <div className="home-reels__overlay" />
      <div className="home-reels__copy">
        <span>{reel.subtitle}</span>
        <h4>{reel.title}</h4>
      </div>
      <div className="home-reels__play">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M8 5v14l11-7z" />
        </svg>
      </div>
    </button>
  );
};

const HomeReels = () => {
  const sectionRef = useRef(null);
  const railRef = useRef(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const pauseUntilRef = useRef(0);
  const modalVideoRef = useRef(null);
  const videoCacheRef = useRef(new Map());
  const inFlightRef = useRef(new Map());
  const [selectedReel, setSelectedReel] = useState(null);
  const [modalVideoSrc, setModalVideoSrc] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [playerError, setPlayerError] = useState(false);

  const getPrimarySource = (reel) => reel?.sources?.[0]?.src || "";

  const cacheVideo = useCallback(async (src) => {
    if (!src) return "";

    try {
      const absoluteUrl = new URL(src, window.location.href);
      if (absoluteUrl.origin !== window.location.origin) {
        return src;
      }
    } catch (e) {
      return src;
    }

    const cached = videoCacheRef.current.get(src);
    if (cached) return cached;

    const pending = inFlightRef.current.get(src);
    if (pending) return pending;

    const request = (async () => {
      const response = await fetch(src, {
        cache: "force-cache",
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`No se pudo cachear el video: ${src}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      videoCacheRef.current.set(src, objectUrl);
      return objectUrl;
    })();

    inFlightRef.current.set(src, request);

    try {
      return await request;
    } finally {
      inFlightRef.current.delete(src);
    }
  }, []);

  const warmupVideos = useCallback(async (reels = []) => {
    for (const reel of reels) {
      const src = getPrimarySource(reel);
      if (!src || videoCacheRef.current.has(src)) continue;

      try {
        await cacheVideo(src);
      } catch (e) {
        // Si el servidor no permite el fetch/cache, seguimos con la URL original.
      }
    }
  }, [cacheVideo]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || REELS.length <= 1) return undefined;

    const step = () => {
      if (Date.now() < pauseUntilRef.current) return;
      const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
      const firstCard = rail.querySelector(".home-reels__card");
      if (!firstCard) return;

      const gap = parseFloat(getComputedStyle(rail).gap || "0") || 0;
      const next = rail.scrollLeft + firstCard.getBoundingClientRect().width + gap;

      if (next >= maxScrollLeft - 4) {
        rail.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        rail.scrollBy({ left: firstCard.getBoundingClientRect().width + gap, behavior: "smooth" });
      }
    };

    timeoutRef.current = window.setTimeout(() => {
      intervalRef.current = window.setInterval(step, 5600);
    }, 1500);

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [warmupVideos]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    let cancelled = false;
    let timeoutId = null;
    const prime = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(() => {
        warmupVideos(REELS.slice(0, 2));
      }, 500);
    };

    if (!("IntersectionObserver" in window)) {
      prime();
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        prime();
        observer.disconnect();
      }
    }, { threshold: 0.2 });

    observer.observe(section);

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
  }, [warmupVideos]);

  useEffect(() => {
    if (!selectedReel) return undefined;
    setPlayerError(false);
    setModalLoading(true);
    setModalVideoSrc("");

    const src = getPrimarySource(selectedReel);
    let cancelled = false;

    cacheVideo(src)
      .catch(() => src)
      .then((resolvedSrc) => {
        if (cancelled) return;
        setModalVideoSrc(resolvedSrc || src);
        setModalLoading(false);

        const currentIndex = REELS.findIndex((reel) => reel.id === selectedReel.id);
        const neighbours = [
          REELS[currentIndex - 1],
          REELS[currentIndex + 1],
        ].filter(Boolean);
        if (neighbours.length) {
          warmupVideos(neighbours);
        }
      });

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedReel(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedReel, cacheVideo, warmupVideos]);

  useEffect(() => {
    if (!selectedReel || !modalVideoSrc) return undefined;

    const video = modalVideoRef.current;
    if (!video) return undefined;

    try {
      video.muted = false;
      video.volume = 1;
      video.load();
      video.play().catch(() => {});
    } catch (e) {}

    const onCanPlay = () => {
      try {
        video.muted = false;
        video.volume = 1;
        video.play().catch(() => {});
      } catch (e) {}
    };

    video.addEventListener("canplay", onCanPlay);
    return () => video.removeEventListener("canplay", onCanPlay);
  }, [selectedReel, modalVideoSrc]);

  useEffect(() => {
    const cache = videoCacheRef.current;
    const inflight = inFlightRef.current;

    return () => {
      cache.forEach((objectUrl) => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch (e) {}
      });
      cache.clear();
      inflight.clear();
    };
  }, []);

  if (!REELS.length) return null;

  return (
    <section className="home-reels" ref={sectionRef}>
      <div className="home-reels__header">
        <div className="home-reels__title">
          <span className="home-reels__eyebrow">Reels</span>
          <h3>Descubre en video</h3>
        </div>
      </div>

      <div
        className="home-reels__rail"
        ref={railRef}
        onTouchStart={() => { pauseUntilRef.current = Date.now() + 8000; }}
        onMouseDown={() => { pauseUntilRef.current = Date.now() + 8000; }}
        onWheel={() => { pauseUntilRef.current = Date.now() + 8000; }}
        onScroll={() => { pauseUntilRef.current = Date.now() + 8000; }}
      >
        {REELS.map((reel) => (
          <ReelCard key={reel.id} reel={reel} onOpen={setSelectedReel} />
        ))}
      </div>

      {selectedReel && createPortal(
        <div className="home-reels__modal" role="dialog" aria-modal="true" onClick={() => setSelectedReel(null)}>
          <div className="home-reels__modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="home-reels__modal-topbar">
              <div className="home-reels__modal-meta">
                <span>{selectedReel.subtitle}</span>
                <h4>{selectedReel.title}</h4>
              </div>
              <button type="button" className="home-reels__close" onClick={() => setSelectedReel(null)} aria-label="Cerrar video">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="home-reels__player-shell">
              {modalLoading && (
                <div className="home-reels__loading">Preparando video...</div>
              )}
              <video
                key={modalVideoSrc || selectedReel.id}
                ref={modalVideoRef}
                className="home-reels__player"
                poster={selectedReel.poster}
                controls
                autoPlay
                playsInline
                preload="auto"
                src={modalVideoSrc || undefined}
                onError={() => setPlayerError(true)}
              >
              </video>
            </div>
            {playerError && (
              <div className="home-reels__fallback">
                <img src={selectedReel.poster} alt={selectedReel.title} />
                <p>No se pudo cargar el video. Intenta de nuevo.</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default HomeReels;
